import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { Store } from '@ngrx/store';
import {catchError, map, switchMap, take, tap} from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import {API_URL, API_URL_V3} from '../constants/api-urls';
import { BaseService } from './base.service';
import { AppSettings } from '../app.settings';
import { Response } from '../models/response.model';
import { PnpBudget } from '../models/pnp-budget.model';
import { isEmptyArray } from '../shared/utils';
import { IAppState } from '../store/app.store';
import {Order} from '../models/order.model';
import {
  fetchPnpBudgets,
  fetchPnpBudgetsSuccess,
  fetchPnpBudgetsFailure,
  fetchPnpBudgetsForOrder,
  fetchPnpBudgetsForOrderSuccess,
  fetchPnpBudgetsForOrderFailure,
  resetPnpBudgetsForOrder,
  fetchBudgetAvailable,
  fetchBudgetAvailableSuccess,
  fetchBudgetAvailableFailure,
  resetBudgetAvailable,
  setPnpBudgetsForOrder,
} from '../store/actions/pnp-budget.actions';
import { selectHasUserBudgets } from '../store/selectors/pnp-budget.selectors';


@Injectable()
export class PnpBudgetService extends BaseService {

    constructor(
      protected http: HttpClient,
      protected appSettings: AppSettings,
      protected toastr: ToastrService,
      private store: Store<IAppState>,
    ) {
        super('', http, appSettings, toastr);
    }

    public fetchPnpBudgets(): Observable<PnpBudget[]> {
        this.store.dispatch(fetchPnpBudgets());

        const url = `${API_URL}/budgets`;
        return this.http.get<Response>(url, {}).pipe(
          map((response: Response) => {
              if (!isEmptyArray(response.data)) {
                  return response.data.map(item => new PnpBudget(item));
              }
              return [];
          }),
          tap((budgets: PnpBudget[]) => {
              this.store.dispatch(fetchPnpBudgetsSuccess({ budgets }));
          }),
          catchError((error) => {
              this.store.dispatch(fetchPnpBudgetsFailure());
              return this.handleError('PnpBudgetService::fetchPnpBudget', [])(error);
          }),
        )
    }

    public fetchPnpBudgetsForOrder(orderId: number, {force, withRecalc}: {force: boolean, withRecalc: boolean}): Observable<PnpBudget[]> {
      if (!orderId || orderId <= 0) {
        return of([]);
      }

      return this.store.select(selectHasUserBudgets).pipe(
        take(1),
        switchMap((hasUserBudgets) => {
          if (!hasUserBudgets && force === false) {
            // if user has not requested budgets before, we don't want to compute budgets for order
            return of([]);
          }

          this.store.dispatch(fetchPnpBudgetsForOrder({ orderId }));

          const url = `${API_URL}/budgets/${orderId}/${withRecalc ? '1' : '0'}`;
          return this.http.get<Response>(url).pipe(
            map((response: Response) => {
                if (!isEmptyArray(response.data)) {
                    return response.data.map(item => new PnpBudget(item));
                }
                return [];
            }),
          );
        }),
        switchMap((orderBudgets: PnpBudget[]) => {
          // still need to fetch all user budgets
          return this.fetchPnpBudgets().pipe(
            map(() => {
              return orderBudgets;
            })
          );
        }),
        tap((budgets: PnpBudget[]) => {
          this.store.dispatch(fetchPnpBudgetsForOrderSuccess({ orderId, budgets }));
        }),
        catchError((error) => {
            this.store.dispatch(fetchPnpBudgetsForOrderFailure({ orderId }));
            return this.handleError('PnpBudgetService::fetchPnpBudgetsForOrder', [])(error);
        }),
      );
    }

  public resetOrderBudgets(order: Order) {
    const orderId = order?.id;
    if (!orderId) {
      return;
    }

    // reset  budgets  for orders
    this.store.dispatch(resetPnpBudgetsForOrder({ orderId }));

    // reset  budget available info
    this.store.dispatch(resetBudgetAvailable({ orderId }));

    // fetch this information  again
    this.fetchBudgetAvailable(order).subscribe();
  }

  public fetchBudgetAvailable(order: Order): Observable<boolean> {
    if (!order || order.id <= 0 || order.item_count <= 0 || !order.isEditable) {
      return of(false);
    }

    this.store.dispatch(fetchBudgetAvailable({ orderId: order.id }));

    const url = `${API_URL_V3}/order/${order.id}/budget-available`;
    return this.http.get<Response>(url).pipe(
      map((response: Response) => response.data),
      tap((available: boolean) => {
        this.store.dispatch(fetchBudgetAvailableSuccess({ orderId: order.id, available }));
      }),
      catchError((error) => {
        this.store.dispatch(fetchBudgetAvailableFailure({ orderId: order.id }));
        return this.handleError('PnpBudgetService::fetchBudgetAvailable', false)(error);
      }),
    );
  }

  public setOrderBudgets(orderId: number, budgets: PnpBudget[]) {
    if (!orderId || orderId <= 0) {
      return;
    }
    this.store.dispatch(setPnpBudgetsForOrder({ orderId, budgets: budgets || [] }));
  }


    // private getMockBudgets(): PnpBudget[] {
    //     return [
    //         new PnpBudget({
    //             id: 1, name: 'Marketing Budget',
    //             budget_ytd: 5000, budget_used: 2000,
    //             budget_remaining: 3000, budget_total: 5000,
    //             group_id: 1,
    //             currency: { code: 'USD', symbol: '$' },
    //             distributors: [{
    //                 pnp_id: 1, pnp_group_id: 'G1',
    //                 assigned_amount: 5000, used_amount: 2000,
    //                 cart_total_amount: 500, remaining_amount: 2500
    //             }],
    //             budget_groups: []
    //         }),
    //         new PnpBudget({
    //             id: 2, name: 'Operations Budget',
    //             budget_ytd: 10000, budget_used: 4000,
    //             budget_remaining: 6000, budget_total: 10000,
    //             group_id: 2,
    //             currency: { code: 'USD', symbol: '$' },
    //             distributors: [],
    //             budget_groups: [{
    //                 pnp_id: 2, pnp_group_id: 'G2',
    //                 assigned_amount: 10000, used_amount: 4000,
    //                 cart_total_amount: 1000, remaining_amount: 5000
    //             }]
    //         }),
    //     ];
    // }
    //
    // private getMockOrderBudgets(): PnpBudget[] {
    //     return [
    //         new PnpBudget({
    //             id: 3, name: 'Order Marketing Budget',
    //             budget_ytd: 3000, budget_used: 1200,
    //             budget_remaining: 1800, budget_total: 3000,
    //             group_id: 1,
    //             currency: { code: 'USD', symbol: '$' },
    //             distributors: [{
    //                 pnp_id: 3, pnp_group_id: 'G1',
    //                 assigned_amount: 3000, used_amount: 1200,
    //                 cart_total_amount: 300, remaining_amount: 1500
    //             }],
    //             budget_groups: []
    //         }),
    //         new PnpBudget({
    //             id: 4, name: 'Order Operations Budget',
    //             budget_ytd: 7000, budget_used: 2500,
    //             budget_remaining: 4500, budget_total: 7000,
    //             group_id: 2,
    //             currency: { code: 'USD', symbol: '$' },
    //             distributors: [],
    //             budget_groups: [{
    //                 pnp_id: 4, pnp_group_id: 'G2',
    //                 assigned_amount: 7000, used_amount: 2500,
    //                 cart_total_amount: 800, remaining_amount: 3700
    //             }]
    //         }),
    //     ];
    // }
}
